import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
    Box,
    Table,
    TableContainer,
    TableRow,
    TableBody,
    Stack,
    Divider,
    Typography,
} from '@mui/material';
import {
    mdiAccountCheck,
    mdiAccountRemove,
    mdiDownload,
    mdiUpload,
    mdiTrashCan,
} from '@mdi/js';

import {
    TableActionBar,
    TableCell,
    TableHead,
    TableSortButton,
    useSelection,
} from 'common/components/Table';
import { FilterBar } from 'common/components/FilterBar';
import { Tooltip } from 'common/components/Tooltip';
import { IconButton } from 'common/components/IconButton';
import { Pagination } from 'common/components/Pagination';
import { Loader } from 'common/components/Loader';
import { useApolloError } from 'common/hooks/useApolloError';
import { useSnackbar } from 'common/hooks/useSnackbar';
import { ConfirmDialog } from 'common/components/ConfirmDialog';
import { Checkbox } from 'common/components/Checkbox';
import { useUserManagementList } from 'user/hooks/userManagement/useUserManagementList';
import { UserManagementTableRow } from 'user/components/UsersManagementTable/UsersManagementTableRow';
import {
    ISortDirection,
    IUserSortBy,
    useArchiveUsersMutation,
    useCurrentUserQuery,
    useExportUsersMutation,
    useDeleteUsersMutation,
} from 'graphql/types';
import { useRouteMatch } from 'route/hooks/useRouteMatch';
import { UserEmailDropdownMenu } from 'user/components/UserEmailDropdownMenu';
import { Button } from 'common/components/Button';
import { Icon } from 'common/components/Icon';
import { Link } from 'common/components/Link';

interface IProps {
    canDeleteUsers?: boolean;
    isEditable?: boolean;
}

export const UsersManagementTable = ({
    canDeleteUsers,
    isEditable,
}: IProps) => {
    const [translate] = useTranslation();
    const [showArchiveDialog, setShowArchiveDialog] = useState(false);
    const [showDeleteDialog, setShowDeleteDialog] = useState(false);
    const { showApolloError } = useApolloError();
    const [displaySnackbar] = useSnackbar();

    const [archiveUsers, { loading: loadingArchiveUsers }] =
        useArchiveUsersMutation();
    const [deleteUsers, { loading: loadingDeleteUsers }] =
        useDeleteUsersMutation();

    const {
        currentSort,
        users,
        loading: usersLoading,
        paginationSettings,
        filters,
        filterValues,
        filterValuesForQuery,
        setPage,
        onSearch,
        onFilterChange,
        onSort,
    } = useUserManagementList();
    const isActiveUsersPage = !!useRouteMatch('USER_MANAGEMENT_LIST');
    const [showExportDialog, setShowExportDialog] = useState(false);
    const [exportReport, { loading: loadingExport }] = useExportUsersMutation();
    const { data: currentUserData } = useCurrentUserQuery();
    const currentUserEmail = currentUserData?.currentUser?.email;

    const loading = usersLoading;

    const resetSettings = () => {
        setPage(1);
        handleSearch('');

        if (!!selected?.length) onSelectAll?.();
    };

    const handleSearch = (searchValue: string) => {
        setPage(1);
        onSearch?.(searchValue);
        if (!!selected?.length) onSelectAll?.();
    };

    const handleArchive = async (archive: boolean) => {
        let succeededIds = [];

        try {
            const response = await archiveUsers({
                variables: { ids: selected, archive: archive },
                update: (cache) => {
                    cache.evict({ fieldName: 'paginatedUsers' });

                    selected.forEach((id) => {
                        cache.evict({ id: `User:${id}` });
                    });

                    cache.gc();
                },
            });

            const archiveUsersResponse = response.data?.archiveUsers?.response;
            const failedIds = archiveUsersResponse?.failedIds || [];
            succeededIds = archiveUsersResponse?.succeededIds || [];

            if (failedIds?.length) {
                displaySnackbar(
                    translate(
                        archive
                            ? 'usersManagementPage.usersArchiveFailed'
                            : 'usersManagementPage.usersUnarchiveFailed',
                        {
                            count: failedIds.length,
                        }
                    ),
                    {
                        variant: 'error',
                    }
                );
            }
        } catch (error) {
            showApolloError(error);

            return;
        }

        setShowArchiveDialog(false);

        resetSettings();

        if (succeededIds.length) {
            displaySnackbar(
                translate(
                    `usersManagementPage.usersActionSuccess.${archive ? 'unarchive' : 'archive'}`,
                    {
                        count: succeededIds.length,
                    }
                ),
                {
                    autoHideDuration: 30000,
                    variant: 'success',
                }
            );
        }
    };

    const handleDelete = async () => {
        let succeededIds = [];

        try {
            const response = await deleteUsers({
                variables: { ids: selected },
                update: (cache) => {
                    cache.evict({ fieldName: 'paginatedUsers' });

                    selected.forEach((id) => {
                        cache.evict({ id: `User:${id}` });
                    });

                    cache.gc();
                },
            });

            const deleteUsersResponse = response.data?.deleteUsers?.response;
            const failedIds = deleteUsersResponse?.failedIds || [];
            succeededIds = deleteUsersResponse?.succeededIds || [];

            if (failedIds?.length) {
                displaySnackbar(
                    translate('usersManagementPage.deleteUsersFailed', {
                        count: failedIds.length,
                    }),
                    {
                        variant: 'error',
                    }
                );
            }
        } catch (error) {
            showApolloError(error);

            return;
        }

        setShowDeleteDialog(false);

        resetSettings();

        if (succeededIds.length) {
            displaySnackbar(
                translate('usersManagementPage.usersActionSuccess.delete', {
                    count: succeededIds.length,
                }),
                {
                    variant: 'success',
                }
            );
        }
    };

    const handleExport = async () => {
        try {
            const exportResults = await exportReport({
                variables: {
                    ...filterValuesForQuery,
                    isActive: isActiveUsersPage,
                    archived: !isActiveUsersPage,
                },
            });
            const fileUrl = exportResults.data?.exportUsers?.fileUrl;

            if (fileUrl) {
                window.open(fileUrl, '_blank');
            } else {
                displaySnackbar(
                    translate('usersManagementPage.exportSuccess'),
                    {
                        variant: 'success',
                    }
                );
            }
        } catch (error) {
            showApolloError(error);

            return;
        }

        setShowExportDialog(false);
    };

    const { ...selectionProps } = useSelection(users);
    const { isSelected, rows, selected, onSelectAll } = selectionProps;

    const tableActions = (
        <Stack
            direction="row"
            divider={<Divider flexItem orientation="vertical" />}
            spacing={1}
            sx={{ justifyContent: 'flex-end', alignItems: 'center' }}
        >
            {canDeleteUsers && (
                <Tooltip
                    title={translate(
                        `usersManagementPage.${isActiveUsersPage ? 'archiveUsers' : 'unarchiveUsers'}`
                    )}
                >
                    <Box>
                        <IconButton
                            color="inherit"
                            disabled={!isSelected}
                            iconPath={
                                isActiveUsersPage
                                    ? mdiAccountRemove
                                    : mdiAccountCheck
                            }
                            iconSize="2.4rem"
                            onClick={() => setShowArchiveDialog(true)}
                        />
                    </Box>
                </Tooltip>
            )}
            {isActiveUsersPage && (
                <UserEmailDropdownMenu
                    disabled={!isSelected}
                    userIds={selected}
                    onSendEmailComplete={() => {
                        if (!!selected?.length) onSelectAll?.();
                    }}
                />
            )}
            {!isActiveUsersPage && (
                <Tooltip title={translate('usersManagementPage.deleteUsers')}>
                    <Box>
                        <IconButton
                            color="inherit"
                            disabled={!isSelected}
                            iconPath={mdiTrashCan}
                            iconSize="2.4rem"
                            onClick={() => setShowDeleteDialog(true)}
                        />
                    </Box>
                </Tooltip>
            )}
        </Stack>
    );

    return (
        <>
            <FilterBar
                useFilterDrawer
                barAppend={
                    <Stack direction="row" spacing={2}>
                        <Button
                            disabled={!users?.length}
                            startIcon={
                                <Icon path={mdiDownload} size="1.9rem" />
                            }
                            sx={{ flex: 1 }}
                            variant="outlined"
                            onClick={() => setShowExportDialog(true)}
                        />

                        <Button
                            component={Link}
                            disabled={!users?.length}
                            startIcon={<Icon path={mdiUpload} size="1.9rem" />}
                            sx={{ flex: 1 }}
                            to="MANAGEMENT_USERS_IMPORT"
                            variant="outlined"
                        />
                    </Stack>
                }
                disabled={loading}
                filterDrawerProps={{
                    title: translate('filterForm.userManagement.title'),
                    description: translate(
                        'filterForm.userManagement.description'
                    ),
                }}
                formFilters={filters}
                formFilterValues={filterValues}
                initialSearchValue={filterValues.q}
                placeholder={translate('filterBarPlaceholder')}
                onFiltersChange={(values) => {
                    setPage(1);
                    onFilterChange(values);
                }}
                onSearch={handleSearch}
                onSearchClear={() => handleSearch('')}
            />

            {!loading && !!rows.length && (
                <TableContainer>
                    <TableActionBar
                        actions={tableActions}
                        selectionProps={selectionProps}
                    />
                    <Table>
                        <TableHead>
                            <TableRow>
                                {isEditable && !!tableActions && (
                                    <TableCell padding="checkbox">
                                        <Checkbox
                                            checked={false}
                                            onChange={onSelectAll}
                                        />
                                    </TableCell>
                                )}
                                <TableCell sx={{ whiteSpace: 'nowrap' }}>
                                    {translate('name')}

                                    <TableSortButton
                                        currentSortDirection={
                                            currentSort.direction
                                        }
                                        currentSortField={
                                            currentSort.field as string
                                        }
                                        sortField={IUserSortBy.Name}
                                        onClick={() => onSort(IUserSortBy.Name)}
                                    />
                                </TableCell>
                                <TableCell>{translate('groups')}</TableCell>
                                <TableCell sx={{ whiteSpace: 'nowrap' }}>
                                    {translate(
                                        isActiveUsersPage
                                            ? 'invitedOn'
                                            : 'deletedOn'
                                    )}

                                    {isActiveUsersPage && (
                                        <TableSortButton
                                            currentSortDirection={
                                                currentSort.direction
                                            }
                                            currentSortField={
                                                currentSort.field as string
                                            }
                                            sortField={
                                                IUserSortBy.InvitationDate
                                            }
                                            onClick={() =>
                                                onSort(
                                                    IUserSortBy.InvitationDate,
                                                    ISortDirection.AscNullsFirst
                                                )
                                            }
                                        />
                                    )}
                                </TableCell>
                                <TableCell sx={{ whiteSpace: 'nowrap' }}>
                                    {translate('lastLogin')}

                                    <TableSortButton
                                        currentSortDirection={
                                            currentSort.direction
                                        }
                                        currentSortField={
                                            currentSort.field as string
                                        }
                                        sortField={IUserSortBy.LastLogin}
                                        onClick={() =>
                                            onSort(
                                                IUserSortBy.LastLogin,
                                                ISortDirection.AscNullsFirst
                                            )
                                        }
                                    />
                                </TableCell>
                                <TableCell>{translate('via')}</TableCell>
                            </TableRow>
                        </TableHead>

                        <TableBody>
                            {rows?.map(({ index, onSelect, isSelected }) => {
                                const user = users[index] || null;

                                if (!user) return null;

                                return (
                                    <UserManagementTableRow
                                        forArchive={!isActiveUsersPage}
                                        isEditable={isEditable}
                                        isSelected={isSelected}
                                        key={user.id}
                                        user={user}
                                        onArchive={resetSettings}
                                        onDelete={resetSettings}
                                        onSelect={onSelect}
                                        onUpdate={resetSettings}
                                    />
                                );
                            })}
                        </TableBody>
                    </Table>
                </TableContainer>
            )}

            {loading && (
                <Box sx={{ position: 'relative', height: '100px', mt: 4 }}>
                    <Loader />
                </Box>
            )}

            {!loading && !users.length && (
                <Box sx={{ mt: 4, textAlign: 'center' }}>
                    <Typography>{translate('noOptionsText.users')}</Typography>
                </Box>
            )}

            {!loading && (
                <Box sx={{ mt: 4 }}>
                    <Pagination
                        page={paginationSettings.page}
                        pageAmount={paginationSettings.pageAmount}
                        totalsAmount={paginationSettings.count}
                        totalsText={
                            paginationSettings.count === 1
                                ? translate('user')
                                : translate('users')
                        }
                        onChange={(page: number) => {
                            setPage(page);
                        }}
                    />
                </Box>
            )}

            <ConfirmDialog
                confirmText={translate(
                    isActiveUsersPage ? 'archiveItem' : 'unarchiveItem'
                )}
                loading={loadingArchiveUsers}
                open={showArchiveDialog}
                title={translate(
                    `usersManagementPage.usersArchiveDialog.${isActiveUsersPage ? 'titleArchive' : 'titleUnarchive'}`
                )}
                onCancel={() => {
                    setShowArchiveDialog(false);
                }}
                onClose={() => setShowArchiveDialog(false)}
                onConfirm={() => handleArchive(isActiveUsersPage)}
            >
                {translate(
                    `usersManagementPage.usersArchiveDialog.${isActiveUsersPage ? 'textArchive' : 'textUnarchive'}`
                )}
            </ConfirmDialog>

            <ConfirmDialog
                confirmText={translate(
                    'usersManagementPage.deleteUsersDialog.confirmText'
                )}
                loading={loadingDeleteUsers}
                open={showDeleteDialog}
                title={translate('usersManagementPage.deleteUsersDialog.title')}
                onCancel={() => setShowDeleteDialog(false)}
                onClose={() => setShowDeleteDialog(false)}
                onConfirm={handleDelete}
            >
                {translate('usersManagementPage.deleteUsersDialog.text')}
            </ConfirmDialog>

            <ConfirmDialog
                confirmText={translate('sendExport')}
                loading={loadingExport}
                open={showExportDialog}
                title={translate(`usersManagementPage.exportDialog.title`)}
                onCancel={() => setShowExportDialog(false)}
                onClose={() => setShowExportDialog(false)}
                onConfirm={handleExport}
            >
                {translate(`usersManagementPage.exportDialog.text`, {
                    email: currentUserEmail,
                })}
            </ConfirmDialog>
        </>
    );
};
